import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction } from 'react-router-dom';

import { Card } from '@/lib/components';
import { closeModal, FloatModal, openModal } from '@/lib/components/Modal/Modal';
import {
  ActivityBannerIcon,
  DeleteIconSolid,
  GroupAddIconSolid,
  SuspendContactIcon,
  TagAddIconSolid,
} from '@/lib/icon';
import { Button, IconSvg, Modal, Spinner, toast } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import {
  ContactsGroupModal,
  ContactsTagModal,
  DeleteModal,
  SuspendContactModal,
} from '@/src/ContactsModule/components/ContactsModals';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';

export type BannerProps = {
  title: string;
  icon?: React.ReactNode;
  buttons?: React.ReactNode[];
  cards?: React.ReactNode[];
};

export const ContactsProfileBanner = ({
  emailId,
  navigate,
}: {
  emailId: number;
  navigate?: NavigateFunction;
}) => {
  const { t } = useTranslation();
  const service = useService();
  const [groups] = useEmblue(ServiceMethods.getGroups, { identifier: 'GROUPS_DROP_DOWN' });
  const groupsListAux = groups?.list.filter((g) => g && g.group && g.group.id).map((g) => g.group);
  const [tags] = useEmblue(ServiceMethods.getTagsDropDown, { identifier: 'TAGS_DROP_DOWN' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const contactBannerProps: BannerProps = {
    title: t('CONTACTS_BANNER.activity'),
    icon: <ActivityBannerIcon color="#004DBC" />,
  };

  useEffect(() => {
    if (confirmDelete) {
      deleteContact().then().catch(console.error);
      setConfirmDelete(false);
    }
  }, [confirmDelete]);

  useEffect(() => {
    if (confirmSuspend) {
      suspendContact().then().catch(console.error);
      setConfirmSuspend(false);
    }
  }, [confirmSuspend]);

  useEffect(() => {
    if (selectedGroups.length >= 1) {
      addContactToGroups().catch(console.error);
    }
  }, [selectedGroups]);

  useEffect(() => {
    if (selectedTags.length >= 1) {
      addContactToTags().catch(console.error);
    }
  }, [selectedTags]);

  const addContactToGroups = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.addContactsToGroup({
      contactsIds: [emailId],
      groupsIds: selectedGroups,
    });
    closeModal(); // spinner
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };
  const addContactToTags = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.addContactsToTags({
      contactsIds: [emailId],
      tagsIds: selectedTags,
    });
    closeModal(); // spinner
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };
  const deleteContact = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.deleteSelectedContacts({ contactsIds: [emailId] });
    closeModal(); // spinner
    if (result) {
      navigate && navigate('/v2/contacts');
    }
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };

  const suspendContact = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.suspendSelectedContacts({ contactsIds: [emailId] });
    closeModal(); // spinner
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };

  return (
    <div className="w-full">
      <Card noPadded>
        <Card.Header borderBottom noPaddingBottom>
          <div className="flex h-full w-full justify-between px-10 py-8">
            <Flex>
              <Flex noGrow withGap alignment="start">
                {contactBannerProps.icon}
                <span className="font-sans text-[24px] font-semibold leading-none text-[#004DBC]">
                  {contactBannerProps.title}
                </span>
              </Flex>
              {
                <Flex withGap alignment="end">
                  <Button
                    outline
                    iconLeft={<DeleteIconSolid />}
                    onClick={() =>
                      openModal({
                        element: (
                          <DeleteModal
                            description={t('CONTACT_DELETE_MODAL.description')}
                            setConfirmDelete={setConfirmDelete}
                            title={t('CONTACT_DELETE_MODAL.title')}
                          />
                        ),
                      })
                    }
                  >
                    {t('delete')}
                  </Button>
                  <Button
                    outline
                    iconLeft={<SuspendContactIcon />}
                    onClick={() => setShowModal(true)}
                  >
                    {t('suspend')}
                  </Button>
                  <Button
                    outline
                    iconLeft={
                      <IconSvg height="30px" svgComponent={<GroupAddIconSolid />} width="30px" />
                    }
                    onClick={() => setShowGroupsModal(true)}
                  >
                    {t('CONTACTS_ACTIONS_DROPDOWN.AddToGroup')}
                  </Button>
                  <Button
                    outline
                    iconLeft={
                      <IconSvg height="30px" svgComponent={<TagAddIconSolid />} width="30px" />
                    }
                    onClick={() => setShowTagsModal(true)}
                  >
                    {t('CONTACTS_ACTIONS_DROPDOWN.AddToTag')}
                  </Button>
                </Flex>
              }
            </Flex>
            <Flex noGrow withGap gapSize="small">
              {contactBannerProps.buttons?.map((button) => button)}
            </Flex>
          </div>
        </Card.Header>
      </Card>
      {showGroupsModal && (
        <ContactsGroupModal
          groups={groupsListAux === undefined ? [{ id: 0, groupName: '' }] : groupsListAux}
          setSelectedGroups={setSelectedGroups}
          setShowGroupsModal={setShowGroupsModal}
        />
      )}
      {showTagsModal && (
        <ContactsTagModal
          setSelectedTags={setSelectedTags}
          setShowTagsModal={setShowTagsModal}
          tags={tags === undefined ? [{ id: 0, name: '' }] : tags.list}
        />
      )}
      <Modal open={showModal} onClose={setShowModal}>
        <SuspendContactModal
          onClick={() => {
            setConfirmSuspend(true);
            setShowModal(false);
          }}
        />
      </Modal>
    </div>
  );
};
